import { supabase } from './supabase';
import { isDemoId } from '../utils/demoUtils';

/**
 * Service pour gérer les recommandations AI dans la base de données
 * Inclut les opérations CRUD (Create, Read, Update, Delete)
 */

interface AIRecommendationData {
  id?: string;
  prospect_id: string;
  recommendations: string[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Sauvegarder des recommandations AI pour un prospect
 * @param prospectId ID du prospect
 * @param recommendations Liste des recommandations à sauvegarder
 * @param analysis (Optionnel) Texte d'analyse personnalisée complète
 * @returns Données des recommandations créées ou mise à jour
 */
export async function saveAIRecommendations(
  prospectId: string,
  recommendations: string[],
  analysis?: string
): Promise<AIRecommendationData | null> {
  try {
    // Vérifier si c'est un ID de démonstration
    if (isDemoId(prospectId)) {
      return {
        id: 'demo-ai-rec-id',
        prospect_id: prospectId,
        recommendations: recommendations,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    // Vérifier si des recommandations existent déjà pour ce prospect
    const { data: existingRecs } = await supabase
      .from('ai_recommendations')
      .select('*')
      .eq('prospect_id', prospectId)
      .maybeSingle();

    let result;

    if (existingRecs) {
      // Mettre à jour les recommandations existantes
      const updateData: any = {
        recommendations: JSON.stringify(recommendations),
        updated_at: new Date().toISOString()
      };

      // Ajouter l'analyse si fournie
      if (analysis !== undefined) {
        updateData.analysis = analysis;
      }

      const { data, error } = await supabase
        .from('ai_recommendations')
        .update(updateData)
        .eq('id', existingRecs.id)
        .select()
        .single();

      if (error) throw error;
      result = data;
    } else {
      // Créer de nouvelles recommandations
      const insertData: any = {
        prospect_id: prospectId,
        recommendations: JSON.stringify(recommendations)
      };

      // Ajouter l'analyse si fournie
      if (analysis !== undefined) {
        insertData.analysis = analysis;
      }

      const { data, error } = await supabase
        .from('ai_recommendations')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      result = data;
    }

    // Formater les données pour correspondre à l'interface
    return {
      id: result.id,
      prospect_id: result.prospect_id,
      recommendations: Array.isArray(result.recommendations)
        ? result.recommendations
        : JSON.parse(result.recommendations),
      created_at: result.created_at,
      updated_at: result.updated_at
    };
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des recommandations AI:', error);
    return null;
  }
}

/**
 * Récupérer les recommandations AI pour un prospect
 * @param prospectId ID du prospect
 * @returns Liste des recommandations ou null si aucune n'est trouvée
 */
export async function getAIRecommendations(
  prospectId: string
): Promise<string[] | null> {
  try {
    
    // Vérifier si c'est un ID de démonstration
    if (isDemoId(prospectId)) {
      return [
        "Améliorer le temps de chargement des pages sur mobile pour une meilleure expérience utilisateur",
        "Optimiser le contenu pour les mots-clés ciblés afin d'améliorer le positionnement sur les moteurs de recherche",
        "Ajouter une stratégie de contenu régulière pour maintenir l'engagement des visiteurs"
      ];
    }
    
    // Vérifier d'abord le nombre d'enregistrements
    const { count, error: countError } = await supabase
      .from('ai_recommendations')
      .select('id', { count: 'exact', head: true })
      .eq('prospect_id', prospectId);
      
    if (countError) throw countError;
    
    // Si aucun enregistrement, retourner null
    if (count === 0) {
      return null;
    }
    
    // S'il y a plusieurs enregistrements, prendre le plus récent
    const { data, error } = await supabase
      .from('ai_recommendations')
      .select('recommendations')
      .eq('prospect_id', prospectId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) throw error;

    if (!data) {
      return null;
    }

    // Convertir les recommandations JSONB en tableau JavaScript
    const recommendations = Array.isArray(data.recommendations) 
      ? data.recommendations 
      : JSON.parse(data.recommendations);

    return recommendations;
  } catch (error) {
    console.error('Erreur lors de la récupération des recommandations AI:', error);
    return null;
  }
}

/**
 * Supprimer les recommandations AI pour un prospect
 * @param prospectId ID du prospect
 * @returns true si supprimé avec succès, false sinon
 */
export async function deleteAIRecommendations(
  prospectId: string
): Promise<boolean> {
  try {
    // Vérifier si c'est un ID de démonstration
    if (isDemoId(prospectId)) {
      return true;
    }

    const { error } = await supabase
      .from('ai_recommendations')
      .delete()
      .eq('prospect_id', prospectId);

    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression des recommandations AI:', error);
    return false;
  }
}

/**
 * Vérifier si des recommandations AI existent pour un prospect
 * @param prospectId ID du prospect
 * @returns true si des recommandations existent, false sinon
 */
export async function hasAIRecommendations(
  prospectId: string
): Promise<boolean> {
  try {
    // Vérifier si c'est un ID de démonstration
    if (isDemoId(prospectId)) {
      return true;
    }

    const { count, error } = await supabase
      .from('ai_recommendations')
      .select('id', { count: 'exact', head: true })
      .eq('prospect_id', prospectId);

    if (error) throw error;
    
    return count !== null && count > 0;
  } catch (error) {
    console.error('Erreur lors de la vérification des recommandations AI:', error);
    return false;
  }
}
